import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import { Languages } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import {
  MapType,
  MapTypeDetails,
  deleteType,
  getType,
  mapTypes,
  publishType,
  unpublishType,
} from '../../../core/services/map'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { CreateEditType } from './CreateEditType'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'

export const MapTypesPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams()
  const { hasEditPermission } = useCheckPermission({ page: 'LOCATIONS_TYPE' })
  const { editionId, festivalName } = useParams()

  const [editCreateModal, setEditCreateModal] = useState(false)
  const [item, setItem] = useState<MapTypeDetails>()
  const [items, setItems] = useState<MapType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        if (allLanguagesResponse.status === 200) {
          setAllLanguages(allLanguagesResponse.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const mapTypeResponse = await mapTypes({
        page,
        size,
        sortBy,
        sortDirection,
      })
      if (mapTypeResponse.status === 200) {
        setItems(mapTypeResponse.data.content)
        setTotalElemenets(mapTypeResponse.data.totalElements)
        setSize(mapTypeResponse.data.size)
        setPage(mapTypeResponse.data.pageNumber + 1)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    sortBy,
    sortDirection,
    setPage,
    setSize,
    setTotalElemenets,
    editionId,
    festivalName,
  ])

  useEffect(() => {
    getData()
  }, [getData])

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(items.map((one) => one.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const handlePublish = (checkedItems: string[]) => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const { status } = await publishType({ ids: checkedItems })
        if (status === 200) {
          getData()
          setIsCheck([])
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleUnpublish = (checkedItems: string[]) => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const { status } = await unpublishType({ ids: checkedItems })
        if (status === 200) {
          getData()
          setIsCheck([])
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        const { status } = await deleteType(isCheck)
        if (status === 204) {
          getData()
          setIsCheck([])
          setIsCheckAll(false)
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),

      width: '16px',
    },
    {
      title: 'Name',
      accessor: 'name',
    },
    {
      title: 'Logo (47X59)',
      accessor: '',
      render: (row) =>
        row?.pinImageUrl ? (
          <img
            src={row.pinImageUrl}
            className="w-[47px] h-[59px] object-contain"
          />
        ) : null,
    },
    {
      title: 'Published',
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={() => {
            if (row.published) {
              handleUnpublish([row.id])
            } else {
              handlePublish([row.id])
            }
          }}
          checked={row.published}
          disabled={!hasEditPermission}
        />
      ),
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            // prettier-ignore
            (async () => {
              try {
                const response = await getType(row.id)
                setEditCreateModal(true)
                setItem(response.data)
              } catch (e) {
                console.error(e)
              }
            })()
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  return (
    <>
      <Header
        pageTitle="Map Types"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish(isCheck) },
                    {
                      label: 'Unpublish',
                      onClick: () => handleUnpublish(isCheck),
                    },
                  ]}
                />
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditCreateModal(true)
                  setItem(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Map" subtitle="Location Types" className="mb-5" />
        <Table
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={items}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />

      <CreateEditType
        setVisible={setEditCreateModal}
        visible={editCreateModal}
        setItems={setItems}
        item={item}
        page={page}
        size={size}
        allLanguages={allLanguages}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Location Type'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
